import React from 'react';
import moment from 'moment';
import isNil from 'lodash/isNil';
import ManagerTeamRankableThings from 'components/dashboard/ManagerTeamRankableThings';
import TeamRankingsSource from 'sources/TeamRankingsSource';
import './ManagerTeamRankings.styl';
import BasedOnDatesLink from 'components/dashboard/common/BasedOnDatesLink';
import MetricTooltip from 'components/pure/tooltips/MetricTooltip';
import {addSubtractDayIntervalEndDateExclusive} from '@store/coaching-plans';
import {dashV2Path} from 'components/StatsPage2.utils';

const TOP_N = 5;

const BEST_REPS_LINK = params =>
  `/${dashV2Path}${params.adminPart}/manager/${params.managerId}/rep-activity`;
const REQUESTED_FEEDBACK_LINK = params =>
  `/inbox/items?unviewedOnly=false&pinnedOnly=false&folderId=${params.inboxId}&tagId=${params.feedbackRequestedTagId}`;
const ENGAGEMENT_LINK = params =>
  `/${dashV2Path}${params.adminPart}/manager/${params.managerId}/self-review`;
const SCORING_LINK = params =>
  `/${dashV2Path}${params.adminPart}/manager/${params.managerId}/rep-skills`;
const AMOUNT_OF_COACHING_LINK = params =>
  `/${dashV2Path}${params.adminPart}/manager/${params.managerId}/coaching-effectiveness`;

const TOOLTIPS = {
  bestReps: (
    <MetricTooltip
      header="Best Reps"
      tooltip="Best Reps are the most engaged, and have had the highest activity levels and call scores during this period. Use your best reps to help coach their peers. Reps at the bottom of this list, or those shown in red, are disengaged and may be less likely to respond to manager coaching."
      link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
    />
  ),
  recentlyRequestedFeedback: (
    <MetricTooltip
      header="Recently Requested Feedback"
      tooltip={`Reps at the top of this list have recently requested feedback on their calls. Keep in mind that reps who frequently raise their hand for feedback outperform their peers.
        Coach recommends that reps request feedback on at least 2 calls a week in order to maintain rep-manager calibration on the rep's performance.`}
      link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
    />
  ),
  engagement: (
    <MetricTooltip
      header="Engagement"
      tooltip="Engagement shows if a rep is invested in their own self-development. Reps that spend 30 minutes to 1 hour per week on self-review activities outperform their peers who don't self-review."
      link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
    />
  ),
  avgRepCallScore: (
    <MetricTooltip
      header="Average Rep Call Score"
      tooltip="Reps are ranked by their Average Call Score during this period. Create a Coaching Plan for any reps with a score of less than 60% on a given skill. "
      link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
    />
  ),
  amtOfCoaching: (
    <MetricTooltip
      header="Amount of Coaching Received"
      tooltip="Are you coaching all of your reps or only some? This could be a good thing or bad thing depending upon the needs of each rep. Research shows that you should focus on B-players and new reps in order to generate a bigger improvement in call quality relative to the amount of time you spend coaching. "
      link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
    />
  ),
};

// Actual tag value in the DB (and auto-created by the BE)
const FEEDBACK_REQUESTED_TAG = 'Feedback Requested';

export default class ManagerTeamRankings extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      currData: {},
      prevData: {},
    };
  }

  componentDidMount() {
    this.getData('currData', this.props.startDate, this.props.endDate);
  }

  componentDidUpdate(prevProps) {
    const {startDate, endDate, selectedTeam} = this.props;
    const {
      startDate: prevStartDate,
      endDate: prevEndDate,
      selectedTeam: prevSelectedTeam,
    } = prevProps;

    if (
      startDate === prevStartDate &&
      endDate === prevEndDate &&
      selectedTeam &&
      prevSelectedTeam &&
      selectedTeam.id === prevSelectedTeam.id
    ) {
      return;
    }
    this.getData('currData', startDate, endDate);
  }

  async getData(label, startDate, endDate) {
    if (!this.props.selectedTeam) {
      return;
    }
    const teamId = this.getTeamId();
    const managerId = this.props.managerId;
    const data = await Promise.all([
      TeamRankingsSource.getBestRepScores(managerId, startDate, endDate, teamId),
      TeamRankingsSource.getFeedbackScores(managerId, startDate, endDate, teamId),
      TeamRankingsSource.getEngagementScores(managerId, startDate, endDate, teamId),
      TeamRankingsSource.getScoringScores(managerId, startDate, endDate, teamId),
      TeamRankingsSource.getCoachingReceivedTeamScores(managerId, startDate, endDate, teamId),
    ]);
    const newData = {};
    newData[label] = {
      bestRepsData: this.transformDataPropsNames(data[0], 0),
      feedbackScores: this.transformDataPropsNames(data[1], null, str => moment(str)),
      engagementScores: this.transformDataPropsNames(data[2], 0),
      scoringScores: this.transformDataPropsNames(data[3], 0),
      coachingReceivedTeamScores: this.transformDataPropsNames(data[4], 0),
    };
    this.setState(newData, () => {
      if (label === 'currData') {
        const {startDate: previousStartDate, endDate: previousEndDate} =
          addSubtractDayIntervalEndDateExclusive('subtract', startDate, endDate);
        this.getData('prevData', previousStartDate, previousEndDate);
      }
    });
  }

  /**
   * Take data of format {first: value1, second: value2} and transform them to {repId: value1, value: value2}
   * Set default value if value2 is nil, and apply transformer if provided
   */
  transformDataPropsNames(dataArray, defaultValue, transformer) {
    return dataArray.map(it => ({
      repId: it.first,
      value: isNil(it.second) ? defaultValue : transformer ? transformer(it.second) : it.second,
    }));
  }

  getTeamId() {
    const {selectedTeam} = this.props;
    return selectedTeam ? selectedTeam.id : null;
  }

  getFeedbackRequestedTagId(inboxId) {
    if (
      !inboxId ||
      !this.props.tagIdsByName ||
      !this.props.tagIdsByName.get(FEEDBACK_REQUESTED_TAG)
    ) {
      return null;
    }
    return this.props.tagIdsByName.get(FEEDBACK_REQUESTED_TAG);
  }

  render() {
    const {coachId, managerId, adminId, inboxId, directReports, startDate, endDate, fromPath} =
      this.props;
    if (!directReports || directReports.isEmpty()) {
      return null;
    }
    const {
      bestRepsData,
      feedbackScores,
      engagementScores,
      scoringScores,
      coachingReceivedTeamScores,
    } = this.state.currData;
    const {
      bestRepsData: prevBestRepsData,
      feedbackScores: prevFeedbackScores,
      engagementScores: prevEngagementScores,
      scoringScores: prevScoringScores,
      coachingReceivedTeamScores: prevCoachingReceivedTeamScores,
    } = this.state.prevData;
    const feedbackRequestedTagId = this.getFeedbackRequestedTagId(inboxId);
    const adminPart = this.props.adminId ? `/admin/${this.props.adminId}` : '';
    const params = {inboxId, feedbackRequestedTagId, managerId, adminPart};
    const sharedProps = {
      directReports,
      adminId,
      managerId,
      coachId,
      topN: TOP_N,
      startDate,
      endDate,
      largerIsBetter: true,
      fromPath,
    };
    return (
      <div className="widget manager-team-rankings">
        <div className="title-and-dates">
          <h2 className="widget-title team-rank-title">Team Rankings</h2>
          <BasedOnDatesLink
            startDate={this.props.headerStartDate}
            endDate={this.props.headerEndDate}
            location={this.props.location}
          />
        </div>
        <div className="rankable-things">
          <ManagerTeamRankableThings
            title={'Best Reps'}
            tooltip={TOOLTIPS.bestReps}
            repValues={bestRepsData}
            prevRepValues={prevBestRepsData}
            link={BEST_REPS_LINK(params)}
            {...sharedProps}
          />
          {/* the link is to the inbox, so admin should not click through (re: showLink) */}
          {!this.props.hideRecentlyRequested && (
            <ManagerTeamRankableThings
              title={'Recently Requested Feedback'}
              tooltip={TOOLTIPS.recentlyRequestedFeedback}
              repValues={feedbackScores}
              prevRepValues={prevFeedbackScores}
              link={feedbackRequestedTagId ? REQUESTED_FEEDBACK_LINK(params) : null}
              {...sharedProps}
            />
          )}
          <ManagerTeamRankableThings
            title={'Engagement'}
            tooltip={TOOLTIPS.engagement}
            repValues={engagementScores}
            prevRepValues={prevEngagementScores}
            link={ENGAGEMENT_LINK(params)}
            {...sharedProps}
          />
          <ManagerTeamRankableThings
            title="Average Rep Call Score"
            tooltip={TOOLTIPS.avgRepCallScore}
            repValues={scoringScores}
            prevRepValues={prevScoringScores}
            link={SCORING_LINK(params)}
            {...sharedProps}
          />
          <ManagerTeamRankableThings
            title={'Amount of Coaching Received'}
            tooltip={TOOLTIPS.amtOfCoaching}
            repValues={coachingReceivedTeamScores}
            prevRepValues={prevCoachingReceivedTeamScores}
            link={AMOUNT_OF_COACHING_LINK(params)}
            {...sharedProps}
          />
        </div>
      </div>
    );
  }
}
